import React from 'react';
import { ExperienceTemplate, TemplateAction, TemplateStep } from 'interface/experience/experience.interface';
import { CONTENT_ACTION_TYPES } from 'store/content/content.type';
import { PUSH_ACTION_TYPES } from 'store/push/push.type';
import { SMS_ACTION_TYPE } from 'store/sms/sms.type';
import { EMAIL_ACTION_TYPE } from 'store/email/email.type';
import { WEBHOOK_ACTION_TYPES } from 'store/webhook/webhook.type';

const MAIN_CLASS = 'templates-library-details';

type IncludeItem = {
  label: string;
  badge: string;
  required: boolean;
};

type TemplatesLibraryDetailsProps = {
  template?: ExperienceTemplate;
};

const REQUIRED_BADGE = { badge: 'Required', required: true };
const OPTIONAL_BADGE = { badge: 'Optional', required: false };

/**
 * Picks the badge for a group of template items. A group counts as required as
 * soon as one of its items is explicitly not optional.
 */
const badgeFor = (items: Array<{ isOptional?: boolean }>): { badge: string; required: boolean } =>
  items.some((item) => item.isOptional === false) ? REQUIRED_BADGE : OPTIONAL_BADGE;

/** True for any of the push action types a template step can carry. */
const isPushAction = (actionType?: string) =>
  actionType === PUSH_ACTION_TYPES.BROADCAST_PUSH ||
  actionType === PUSH_ACTION_TYPES.TRIGERRED_PUSH ||
  actionType === PUSH_ACTION_TYPES.PUSH_NOTIFICATION;

/** True for any of the in-app content action types a template step can carry. */
const isContentAction = (actionType?: string) =>
  actionType === CONTENT_ACTION_TYPES.INSTANCE ||
  actionType === CONTENT_ACTION_TYPES.BLUEPRINT ||
  actionType === CONTENT_ACTION_TYPES.GROUPING;

/** Flattens the actions of every step into a single list. */
const getStepActions = (steps: TemplateStep[] = []): TemplateAction[] =>
  steps.flatMap((step) => step.actions || []);

/**
 * Builds the "Template Includes" list shown in the details panel by inspecting
 * the template's audience rules, schedule constraints and step actions. Journey
 * Schedule is always appended because it can be configured on any template.
 */
const getTemplateIncludes = (template: ExperienceTemplate): IncludeItem[] => {
  const steps = template.steps || [];
  const actions = getStepActions(steps);
  const includes: IncludeItem[] = [];

  const audienceRules = steps.flatMap((step) =>
    [step.audience?.preferred, step.audience?.restricted].filter(
      (rule): rule is NonNullable<typeof rule> => Boolean(rule),
    ),
  );
  if (audienceRules.length) {
    includes.push({ label: 'Audience Rules', ...badgeFor(audienceRules) });
  }

  if (steps.some((step) => step.constraints?.audienceConstraints?.syncStartScheduleWithRuleBody)) {
    includes.push({ label: 'Rule Schedule', badge: 'Based on Audience', required: false });
  }

  const pushActions = actions.filter((action) => isPushAction(action.actionType));
  if (pushActions.length) {
    includes.push({ label: 'Push', ...badgeFor(pushActions) });
  }

  const contentActions = actions.filter((action) => isContentAction(action.actionType));
  if (contentActions.length) {
    includes.push({ label: 'In-App Content', ...badgeFor(contentActions) });
  }

  const smsActions = actions.filter((action) => action.actionType === SMS_ACTION_TYPE.SMS);
  if (smsActions.length) {
    includes.push({ label: 'SMS', ...badgeFor(smsActions) });
  }

  const emailActions = actions.filter((action) => action.actionType === EMAIL_ACTION_TYPE.EMAIL);
  if (emailActions.length) {
    includes.push({ label: 'Email', ...badgeFor(emailActions) });
  }

  const webhookActions = actions.filter((action) => action.actionType === WEBHOOK_ACTION_TYPES.WEBHOOK);
  if (webhookActions.length) {
    includes.push({ label: 'Webhook', ...badgeFor(webhookActions) });
  }

  includes.push({ label: 'Journey Schedule', ...OPTIONAL_BADGE });

  return includes;
};

/**
 * Right-hand details panel of the templates library modal. Renders nothing
 * until a template is selected.
 */
export const TemplatesLibraryDetails: React.FC<TemplatesLibraryDetailsProps> = ({ template }) => {
  if (!template) {
    return null;
  }

  const includes = getTemplateIncludes(template);

  return (
    <aside className={MAIN_CLASS}>
      <div className={`${MAIN_CLASS}__description`}>
        <h3 className={`${MAIN_CLASS}__title`}>{template.name}</h3>
        {template.desc ? <p className={`${MAIN_CLASS}__copy`}>{template.desc}</p> : null}
      </div>
      {includes.length ? (
        <>
          <div className={`${MAIN_CLASS}__divider`} />
          <div className={`${MAIN_CLASS}__includes`}>
            <h4 className={`${MAIN_CLASS}__includes-title`}>Template Includes:</h4>
            <ul className={`${MAIN_CLASS}__includes-list`}>
              {includes.map((item) => (
                <li key={item.label} className={`${MAIN_CLASS}__includes-item`}>
                  <span className={`${MAIN_CLASS}__includes-left`}>
                    <span
                      className={`${MAIN_CLASS}__bullet${item.required ? ` ${MAIN_CLASS}__bullet--required` : ''}`}
                      aria-hidden
                    />
                    {item.label}
                  </span>
                  <span className={`${MAIN_CLASS}__badge${item.required ? ` ${MAIN_CLASS}__badge--required` : ''}`}>
                    {item.badge}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : null}
      {/* TODO: Need to replace this when the verbiage is finalized*/}
      <div className={`${MAIN_CLASS}__callout`}>
        <p className={`${MAIN_CLASS}__callout-title`}>Using context attribute for your audience rules?</p>
        <p className={`${MAIN_CLASS}__callout-copy`}>
          Be sure to upload and process your CSV file so those attributes are ready to use.
        </p>
        <p className={`${MAIN_CLASS}__callout-title`}>When will my notification be delivered?</p>
        <p className={`${MAIN_CLASS}__callout-copy`}>
          When a customer qualifies as an audience, they will receive the notification immediately so long as the
          experience is live.
        </p>
      </div>
    </aside>
  );
};
